import { useEffect, useState } from 'react';
import { Building2, Plus, Edit, Trash2, Save, Eye, EyeOff, Users } from 'lucide-react';
import { useToastStore } from '../store';
import { Spinner, Modal, Badge } from '../components/ui';
import { api } from '../utils/api';

interface UserWithTenant {
  id: string;
  username: string;
  email: string;
  display_name: string | null;
  role: string;
  status: string;
  tenant_id: string;
  tenant_name: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  subdomain: string | null;
  plan: 'free' | 'basic' | 'pro' | 'enterprise';
  status: 'Active' | 'Suspended' | 'Cancelled';
  created_at: string;
  updated_at: string;
}

export function SuperAdminPage() {
  const { addToast } = useToastStore();
  const [isLoading, setIsLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subdomain: '',
    plan: 'pro' as 'free' | 'basic' | 'pro' | 'enterprise',
    status: 'Active' as 'Active' | 'Suspended' | 'Cancelled',
    admin: {
      username: '',
      email: '',
      password: '',
      display_name: ''
    }
  });
  const [showPassword, setShowPassword] = useState(false);

  // Reassign user to tenant (for fixing "can't see my data" when user is in wrong org)
  const [usersWithTenants, setUsersWithTenants] = useState<UserWithTenant[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [reassigningId, setReassigningId] = useState<string | null>(null);
  const [moveToTenantId, setMoveToTenantId] = useState<Record<string, string>>({});

  useEffect(() => {
    loadTenants();
  }, []);

  // Refresh tenants when page becomes visible (e.g., after importing from another page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadTenants();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      const result = await api.getTenants();
      if (result.success && result.data) {
        const tenantsArray = Array.isArray(result.data) ? result.data : [];
        setTenants(tenantsArray);
      } else {
        addToast(result.error || 'Failed to load tenants', 'error');
      }
    } catch (error) {
      addToast('Failed to load tenants', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsersWithTenants = async () => {
    setLoadingUsers(true);
    try {
      const result = await api.getUsersWithTenants();
      if (result.success && result.data) {
        setUsersWithTenants(result.data as UserWithTenant[]);
      }
    } catch {
      addToast('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleReassignUser = async (userId: string) => {
    const tenantId = moveToTenantId[userId];
    if (!tenantId) {
      addToast('Select a tenant first', 'warning');
      return;
    }
    setReassigningId(userId);
    try {
      const result = await api.reassignUserToTenant(userId, tenantId);
      if (result.success) {
        addToast('User moved to that organization. They should refresh the page or log in again.', 'success');
        loadUsersWithTenants();
        setMoveToTenantId(prev => { const next = { ...prev }; delete next[userId]; return next; });
      } else {
        addToast(result.error || 'Failed to move user', 'error');
      }
    } catch {
      addToast('Failed to move user', 'error');
    } finally {
      setReassigningId(null);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.slug) {
      addToast('Name and slug are required', 'warning');
      return;
    }

    // Validate slug format (lowercase, alphanumeric, hyphens only)
    const slugRegex = /^[a-z0-9-]+$/;
    const normalizedSlug = formData.slug.toLowerCase().trim();
    if (!slugRegex.test(normalizedSlug)) {
      addToast('Slug must contain only lowercase letters, numbers, and hyphens', 'warning');
      return;
    }

    // Validate admin fields if admin is being created
    if (formData.admin.username) {
      if (!formData.admin.email || !formData.admin.password) {
        addToast('Admin email and password are required when creating an admin user', 'warning');
        return;
      }
    }

    setIsSaving(true);
    try {
      const result = await api.createTenant({
        name: formData.name.trim(),
        slug: normalizedSlug,
        subdomain: formData.subdomain?.trim() || null,
        plan: formData.plan,
        status: formData.status,
        admin: formData.admin.username?.trim() ? {
          username: formData.admin.username.trim(),
          email: formData.admin.email.trim(),
          password: formData.admin.password,
          display_name: formData.admin.display_name?.trim() || undefined
        } : undefined
      });

      if (result.success) {
        addToast('Tenant created successfully', 'success');
        setShowCreateModal(false);
        resetForm();
        loadTenants();
      } else {
        addToast(result.error || 'Failed to create tenant', 'error');
      }
    } catch (error: any) {
      console.error('Error creating tenant:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create tenant';
      addToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      subdomain: tenant.subdomain || '',
      plan: tenant.plan,
      status: tenant.status,
      admin: {
        username: '',
        email: '',
        password: '',
        display_name: ''
      }
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingTenant) return;

    setIsSaving(true);
    try {
      const result = await api.updateTenant(editingTenant.id, {
        name: formData.name,
        slug: formData.slug,
        subdomain: formData.subdomain || null,
        plan: formData.plan,
        status: formData.status
      });

      if (result.success) {
        addToast('Tenant updated successfully', 'success');
        setShowEditModal(false);
        setEditingTenant(null);
        resetForm();
        loadTenants();
      } else {
        addToast(result.error || 'Failed to update tenant', 'error');
      }
    } catch (error) {
      addToast('Failed to update tenant', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (tenantId: string, tenantName: string) => {
    const message = `Deactivate this organization?\n\n"${tenantName}"\n\nIt will be marked as inactive. No data will be deleted — users won't be able to log in to this org until you reactivate it (via Edit → set status to Active).`;
    if (!confirm(message)) {
      return;
    }

    try {
      const result = await api.deleteTenant(tenantId);
      if (result.success) {
        addToast('Tenant deactivated. Data preserved; reactivate from Edit if needed.', 'success');
        loadTenants();
      } else {
        addToast(result.error || 'Failed to deactivate tenant', 'error');
      }
    } catch (error) {
      addToast('Failed to deactivate tenant', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      subdomain: '',
      plan: 'pro',
      status: 'Active',
      admin: {
        username: '',
        email: '',
        password: '',
        display_name: ''
      }
    });
    setShowPassword(false);
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'purple';
      case 'pro': return 'info';
      case 'basic': return 'success';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tenant Management</h1>
          <p className="text-slate-500 mt-1">Manage organizations and their settings</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          Create Tenant
        </button>
      </div>

      {/* Reassign user to tenant - for when someone can't see their data because they're in the wrong org */}
      <div className="card p-6 mb-6 border-amber-200 bg-amber-50/50">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <Users className="w-5 h-5" />
          Reassign user to organization
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          If you can&apos;t see your schedule or data, your login may be tied to the wrong organization. Load the list below, find your user, and move them to the organization that has the data.
        </p>
        <button
          type="button"
          onClick={loadUsersWithTenants}
          disabled={loadingUsers}
          className="btn btn-secondary mb-4"
        >
          {loadingUsers ? <Spinner className="w-4 h-4" /> : 'Load users and their organizations'}
        </button>
        {usersWithTenants.length > 0 && (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-medium text-slate-700">User</th>
                  <th className="text-left p-3 font-medium text-slate-700">Current organization</th>
                  <th className="text-left p-3 font-medium text-slate-700">Move to</th>
                  <th className="p-3 w-24" />
                </tr>
              </thead>
              <tbody>
                {usersWithTenants.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <span className="font-medium text-slate-900">{u.display_name || u.username}</span>
                      <span className="text-slate-500 block text-xs">{u.email}</span>
                    </td>
                    <td className="p-3 text-slate-600">{u.tenant_name}</td>
                    <td className="p-3">
                      <select
                        className="input w-full max-w-[200px]"
                        value={moveToTenantId[u.id] ?? ''}
                        onChange={(e) => setMoveToTenantId(prev => ({ ...prev, [u.id]: e.target.value }))}
                      >
                        <option value="">Select organization</option>
                        {tenants.filter(t => t.id !== u.tenant_id).map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => handleReassignUser(u.id)}
                        disabled={!moveToTenantId[u.id] || reassigningId === u.id}
                        className="btn btn-primary btn-sm"
                      >
                        {reassigningId === u.id ? <Spinner className="w-4 h-4" /> : 'Move'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {tenants.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No tenants yet</h3>
            <p className="text-slate-500 mb-4">Create your first tenant to get started</p>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4" />
              Create Tenant
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tenants.map(tenant => (
            <div key={tenant.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">{tenant.name}</h3>
                  <p className="text-sm text-slate-500">@{tenant.slug}</p>
                  {tenant.subdomain && (
                    <p className="text-xs text-slate-400 mt-1">{tenant.subdomain}</p>
                  )}
                </div>
                <Badge variant={getPlanColor(tenant.plan)}>{tenant.plan}</Badge>
              </div>
              
              <div className="flex items-center gap-2 mb-4">
                <Badge variant={tenant.status === 'Active' ? 'success' : 'danger'}>
                  {tenant.status}
                </Badge>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(tenant)}
                  className="btn btn-secondary btn-sm flex-1"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                {tenant.status !== 'Cancelled' && (
                <button
                  onClick={() => handleDeactivate(tenant.id, tenant.name)}
                  className="btn btn-danger btn-sm"
                  title="Deactivate (mark inactive; data is preserved)"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Tenant Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create Tenant"
        size="lg"
      >
        <TenantForm
          formData={formData}
          setFormData={setFormData}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isSaving={isSaving}
          onSubmit={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          mode="create"
        />
      </Modal>

      {/* Edit Tenant Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingTenant(null);
          resetForm();
        }}
        title="Edit Tenant"
        size="lg"
      >
        <TenantForm
          formData={formData}
          setFormData={setFormData}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isSaving={isSaving}
          onSubmit={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setEditingTenant(null);
            resetForm();
          }}
          mode="edit"
        />
      </Modal>
    </div>
  );
}

function TenantForm({
  formData,
  setFormData,
  showPassword,
  setShowPassword,
  isSaving,
  onSubmit,
  onCancel,
  mode
}: {
  formData: any;
  setFormData: (data: any) => void;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  isSaving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  mode: 'create' | 'edit';
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="input-label">Tenant Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input"
          placeholder="Acme Corporation"
          required
        />
      </div>

      <div>
        <label className="input-label">Slug *</label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
          className="input"
          placeholder="acme-corp"
          required
        />
        <p className="text-xs text-slate-500 mt-1">Lowercase letters, numbers, and hyphens only</p>
      </div>

      <div>
        <label className="input-label">Subdomain (optional)</label>
        <input
          type="text"
          value={formData.subdomain}
          onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
          className="input"
          placeholder="acme"
        />
        <p className="text-xs text-slate-500 mt-1">For subdomain-based tenant routing (e.g., acme.yourdomain.com)</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="input-label">Plan</label>
          <select
            value={formData.plan}
            onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
            className="input"
          >
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>

        <div>
          <label className="input-label">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="input"
          >
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {mode === 'create' && (
        <div className="border-t border-slate-200 pt-4 mt-4">
          <h4 className="font-medium text-slate-900 mb-3">Create Admin User (Optional)</h4>
          <p className="text-sm text-slate-500 mb-2">
            Create an admin user for this tenant. You can skip this and create users later.
          </p>
          <p className="text-sm text-amber-700 mb-4">
            Use a username and email that are not already used in another organization. Reusing your first tenant&apos;s credentials would create a duplicate and can affect who you log in as.
          </p>

          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="input-label">Username</label>
                <input
                  type="text"
                  value={formData.admin.username}
                  onChange={(e) => setFormData({
                    ...formData,
                    admin: { ...formData.admin, username: e.target.value }
                  })}
                  className="input"
                  placeholder="admin"
                />
              </div>
              <div>
                <label className="input-label">Email</label>
                <input
                  type="email"
                  value={formData.admin.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    admin: { ...formData.admin, email: e.target.value }
                  })}
                  className="input"
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            <div>
              <label className="input-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.admin.password}
                  onChange={(e) => setFormData({
                    ...formData,
                    admin: { ...formData.admin, password: e.target.value }
                  })}
                  className="input pr-10"
                  placeholder="Minimum 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="input-label">Display Name</label>
              <input
                type="text"
                value={formData.admin.display_name}
                onChange={(e) => setFormData({
                  ...formData,
                  admin: { ...formData.admin, display_name: e.target.value }
                })}
                className="input"
                placeholder="Admin User"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 justify-end pt-4">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={isSaving || !formData.name || !formData.slug}
          className="btn btn-primary"
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {mode === 'create' ? 'Create Tenant' : 'Save Changes'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

